import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { v4 as uuidv4 } from "uuid";
import { AddEditGiConfigurationPayload } from "../../../../data-model/addEditGiConfigrationPayload";
import { GiConfigurationForAddNewViewTab } from "../../../../data-model/giConfigDetails";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { addEditGiConfiguration } from "../../../../../shared/api/postCall/addEditGiConfiguration";
import { GroupInboxRoute } from "../../../../../shared/api/services/groupInbox/groupInboxConfigTypes";
import {
  ADD_NEW_VIEW_TAB_TYPE,
  EDIT_VIEW_TAB_TYPE,
  ERROR_UPDATING_GI_CONFIGURATION,
  GI_CONFIGURATION_UPDATED_SUCCESSFULLY,
} from "../../../../../shared/constants/stringLiterals";

/**
 * Persists a new or edited GI view-tab configuration through the push-config API.
 *
 * Sends `route` so the backend writes into the matching Firestore collection
 * (`rpa_config` vs `rpa_config_api`). On success, closes the modal and calls
 * `onConfigSaved` so the list can refetch.
 *
 * @param giConfiguration - Form values for the view tab being saved
 * @param type - Whether this is an add or an edit
 * @param shouldUpdateConfig - When editing, whether the JSON config changed
 * @param org_id - Organization that owns the configuration
 * @param route - Configuration source (`rpa` or `api`) the document belongs to
 * @param dispatch - Redux dispatch used to close the modal
 * @param setLoading - Toggles the modal save-button loading state
 * @param onConfigSaved - Invoked after a successful save (typically a list refresh)
 */
export const handleGiConfigUpdate = async (
  giConfiguration: GiConfigurationForAddNewViewTab,
  type: typeof ADD_NEW_VIEW_TAB_TYPE | typeof EDIT_VIEW_TAB_TYPE,
  shouldUpdateConfig: boolean,
  org_id: string,
  route: GroupInboxRoute,
  dispatch: AppDispatch,
  setLoading: (loading: boolean) => void,
  onConfigSaved: () => void,
) => {
  setLoading(true);
  let payload: AddEditGiConfigurationPayload;
  if (type === ADD_NEW_VIEW_TAB_TYPE) {
    payload = {
      org_id: org_id,
      meta: {
        view_tab: giConfiguration.view_tab,
        version: uuidv4(),
        num_orders: giConfiguration.num_orders,
      },
      config: giConfiguration.config,
      route: route,
    };
  } else {
    payload = {
      org_id: org_id,
      meta: {
        view_tab: giConfiguration.view_tab,
        version: giConfiguration.version.toString(),
        num_orders: giConfiguration.num_orders,
        force_update: shouldUpdateConfig,
      },
      ...(shouldUpdateConfig ? { config: giConfiguration.config } : {}),
      route: route,
    };
  }

  await addEditGiConfiguration(payload, {
    onSuccess: () => {
      dispatch(closeModal());
      toast.success(GI_CONFIGURATION_UPDATED_SUCCESSFULLY);
      onConfigSaved();
    },
    onError: () => {
      dispatch(closeModal());
      toast.error(ERROR_UPDATING_GI_CONFIGURATION);
    },
  });
  setLoading(false);
};
